'use client'

import React, { useRef, useEffect, useState } from 'react'
import { cn } from '@/utilities/ui'
import { usePricingToggle, type PricingPeriod } from '@/providers/PricingToggle'

interface PricingToggleProps {
  className?: string
}

/**
 * Hourly/Monthly pricing toggle for table blocks.
 * Connects to the global PricingToggle provider to synchronize state across tables.
 */
export const PricingToggle: React.FC<PricingToggleProps> = ({ className }) => {
  const { pricingPeriod, setPricingPeriod } = usePricingToggle()
  const hourlyRef = useRef<HTMLButtonElement>(null)
  const monthlyRef = useRef<HTMLButtonElement>(null)
  const [sliderStyle, setSliderStyle] = useState({ width: 0, left: 0 })

  // Update slider position when period changes or on mount
  useEffect(() => {
    const activeRef = pricingPeriod === 'hourly' ? hourlyRef : monthlyRef
    if (activeRef.current) {
      setSliderStyle({
        width: activeRef.current.offsetWidth,
        left: activeRef.current.offsetLeft,
      })
    }
  }, [pricingPeriod])

  const handleToggle = (period: PricingPeriod) => {
    setPricingPeriod(period)
  }

  return (
    <div
      className={cn('relative inline-flex items-center p-1 rounded-full border border-civo-black', className)}
      role="tablist"
      aria-label="Pricing period toggle"
    >
      {/* Sliding background pill */}
      <div
        className="absolute top-1 h-[calc(100%-8px)] bg-civo-black rounded-full transition-all duration-300 ease-out"
        style={{
          width: sliderStyle.width,
          left: sliderStyle.left,
        }}
      />

      <button
        ref={hourlyRef}
        type="button"
        role="tab"
        aria-selected={pricingPeriod === 'hourly'}
        onClick={() => handleToggle('hourly')}
        className={cn(
          'relative z-10 px-4 py-2 rounded-full text-sm leading-tight tracking-tight font-sans font-medium transition-colors duration-300',
          pricingPeriod === 'hourly' ? 'text-white' : 'text-civo-black',
        )}
      >
        Hourly
      </button>
      <button
        ref={monthlyRef}
        type="button"
        role="tab"
        aria-selected={pricingPeriod === 'monthly'}
        onClick={() => handleToggle('monthly')}
        className={cn(
          'relative z-10 px-4 py-2 rounded-full text-sm leading-tight tracking-tight font-sans font-medium transition-colors duration-300',
          pricingPeriod === 'monthly' ? 'text-white' : 'text-civo-black',
        )}
      >
        Monthly
      </button>
    </div>
  )
}
